import gsap from "gsap";

class SplitButton {
  private splitButton: HTMLElement;
  private spans: HTMLElement[] = [];
  private arrowLeft: HTMLElement | null = null;
  private arrowRight: HTMLElement | null = null;
  private hoverHandler: (() => void) | null = null;
  private leaveHandler: (() => void) | null = null;

  constructor(blockquoteEl: HTMLElement) {
    this.splitButton = blockquoteEl;
  }

  public init() {
    if (!this.splitButton) return;

    this.spans = Array.from(this.splitButton.querySelectorAll("span"));
    this.arrowLeft = this.splitButton.querySelector(".splitbutton-arrowleft");
    this.arrowRight = this.splitButton.querySelector(".splitbutton-arrowright");

    // Bind handlers so we can remove them later if needed
    this.hoverHandler = this.onHover.bind(this);
    this.leaveHandler = this.onLeave.bind(this);

    this.splitButton.addEventListener("mouseenter", this.hoverHandler);
    this.splitButton.addEventListener("mouseleave", this.leaveHandler);
  }

  private onHover() {
    const total = this.spans.length;
    const center = (total - 1) / 2;

    const maxSpreadPerWord = parseInt(this.splitButton.dataset.spread ?? "20", 10);
    const totalSpread = center * maxSpreadPerWord;
    const arrowSpread = maxSpreadPerWord;
    const spreadDuration = 0.5;

    this.spans.forEach((span, i) => {
      const offset = i - center;
      const direction = offset === 0 ? 0 : offset > 0 ? 1 : -1;
      const abs = Math.abs(offset);
      const targetY = -2 - abs * 6;

      gsap.to(span, {
        y: targetY,
        x: direction * abs * maxSpreadPerWord,
        rotate: direction * (2 + abs * 1),
        duration: spreadDuration,
        ease: "power2.out",
        onComplete: () => {
          const floatAmp = 3 + Math.random() * 8;
          const floatDir = i % 2 === 0 ? 1 : -1;
          gsap.to(span, {
            y: targetY + floatDir * floatAmp,
            duration: 2 + i * 0.07,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
          });
        },
      });
    });

    const arrowOffset = totalSpread + arrowSpread;
    const arrowTargetY = -2;
    if (this.arrowLeft) {
      const floatDir = 0 % 2 === 0 ? 1 : -1; // index 0 → even → up
      gsap.to(this.arrowLeft, {
        y: arrowTargetY,
        x: -arrowOffset,
        rotate: -2,
        duration: spreadDuration,
        ease: "power2.out",
        onComplete: () => {
          gsap.to(this.arrowLeft!, {
            y: arrowTargetY + floatDir * (3 + Math.random() * 8),
            duration: 2.75,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
          });
        },
      });
    }
    if (this.arrowRight) {
      const floatDir = (total + 1) % 2 === 0 ? 1 : -1; // index total+1
      gsap.to(this.arrowRight, {
        y: arrowTargetY,
        x: arrowOffset,
        rotate: 2,
        duration: spreadDuration,
        ease: "power2.out",
        onComplete: () => {
          gsap.to(this.arrowRight!, {
            y: arrowTargetY + floatDir * (3 + Math.random() * 8),
            duration: 2.65,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
          });
        },
      });
    }
  }

  private onLeave() {
    const unspreadDuration = 0.3;

    const allElements: HTMLElement[] = [
      ...(this.arrowLeft ? [this.arrowLeft] : []),
      ...this.spans,
      ...(this.arrowRight ? [this.arrowRight] : []),
    ];

    allElements.forEach((el) => {
      gsap.killTweensOf(el);
      gsap.to(el, {
        y: 0,
        x: 0,
        rotate: 0,
        duration: unspreadDuration,
        ease: "power2.inOut",
      });
    });
  }

  public destroy() {}
}

// initialize multiple instances
function initSplitButtons() {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>(".splitbutton")
  );
  if (nodes.length === 0) return;
  const instances: SplitButton[] = [];
  for (const el of nodes) {
    const instance = new SplitButton(el);
    instance.init();
    instances.push(instance);
  }
  (window as any)._splitButtonInstances = instances;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSplitButtons);
} else {
  initSplitButtons();
}
